import type { ResourceListOptions } from '#dn-types/api.ts';
import type { FirewallRule, Role, RoleListItem } from '#dn-types/role.ts';
import { rewordValidationMessages } from './errors.ts';
import { del, get, getList, post, put } from './fetch.ts';

export const DEFAULT_ROLES_PAGE_SIZE = 25;

// GET /roles
export type GetRolesResult = RoleListItem[];
type GetRolesVariables = ResourceListOptions;
export function getRoles({ cursor = '', pageSize = DEFAULT_ROLES_PAGE_SIZE, init }: GetRolesVariables = {}) {
  return getList<GetRolesResult>(
    `/v1/roles?includeCounts=true&cursor=${encodeURIComponent(cursor)}&pageSize=${pageSize}`,
    init
  ).then((res) => {
    // TODO: DRY this up with hosts
    const page = res.metadata.page;
    // If the user paginates backwards, we may send a cursor which results in loading the "first" page of results
    // but with fewer results than the standard page size. Refetch the first page without a cursor to get a full result set.
    if (page?.start === 0 && page.count < pageSize && cursor) {
      return getList<GetRolesResult>(`/v1/roles?includeCounts=true&pageSize=${pageSize}`);
    }
    return res;
  });
}

// GET /roles/:id
export type GetRoleResult = Role;
export function getRole(id: string, init?: RequestInit) {
  return get<GetRoleResult>(`/v1/roles/${encodeURIComponent(id)}`, init);
}

// PUT /roles/:id
export type EditRoleVariables = {
  id: string;
  values: {
    description: string;
    firewallRules: Array<Omit<FirewallRule, 'id' | 'roleID'>>;
  };
};
export type EditRoleResult = Role;
export function editRole(input: EditRoleVariables) {
  const { id, values } = input;
  return put<EditRoleResult>(`/v1/roles/${encodeURIComponent(id)}`, values).catch((err: unknown) => {
    rewordValidationMessages(err, (details) => {
      if (
        details.path &&
        /firewallRules\[\d+\]\.allowedTags/.test(details.path) &&
        details.code === 'ERR_LIMIT_EXCEEDED'
      ) {
        if (typeof details.extra?.limit === 'number') {
          return `You have reached the limit of ${details.extra.limit} tags for one or more of your firewall rules. Remove some of the tags before submitting again.`;
        }
        return 'You have the maximum number of tags allowed for one or more of your firewall rules. Remove some of the tags before submitting again.';
      }
    });
  });
}

// POST /roles
export type AddRoleVariables = {
  name: string;
  description?: string;
  firewallRules: Array<Omit<FirewallRule, 'id' | 'roleID'>>;
};
export type AddRoleResult = Role;
export function addRole(formData: AddRoleVariables) {
  return post<AddRoleResult>('/v1/roles', formData).catch((err: unknown) => {
    rewordValidationMessages(err, (details) => {
      if (details.path === 'name' && details.code === 'ERR_DUPLICATE_VALUE') {
        return 'A role with this name already exists.';
      }
      if (
        details.path &&
        /firewallRules\[\d+\]\.allowedTags/.test(details.path) &&
        details.code === 'ERR_LIMIT_EXCEEDED'
      ) {
        if (typeof details.extra?.limit === 'number') {
          return `You have reached the limit of ${details.extra.limit} tags for one or more of your firewall rules. Remove some of the tags before submitting again.`;
        }
        return 'You have the maximum number of tags allowed for one or more of your firewall rules. Remove some of the tags before submitting again.';
      }
    });
  });
}

// DELETE /roles/:id
export type DeleteRoleResult = Record<string, never>;
export function deleteRole(id: string) {
  return (
    del<DeleteRoleResult>(`/v1/roles/${encodeURIComponent(id)}`)
      // Convert from server error codes to more friendly strings, if possible
      .catch((err: unknown) => {
        rewordValidationMessages(err, (details) => {
          if (details.path === undefined && details.code === 'ERR_HAS_DEPENDENTS') {
            // The API sends this code, with no path, for every kind of dependent. DeleteRoleDialog's pre-check can
            // miss one (added after it ran, a failed lookup, ROLE_SCOPED_API_KEYS off), so this can't claim what it ruled out.
            return "This role is still in use. If no hosts, endpoint SSO providers, or API keys use it, it's a firewall rule in another role, route, or tag. Remove the role from whatever uses it, then try again.";
          }
        });
      })
  );
}
